import { useTitleStore } from 'src/stores/useTitleStore';
import * as React from 'react';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { useIntl } from 'react-intl';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { booleanBadgeOptions, ICON_MAP } from 'src/utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useGetSubscription } from 'src/features/subscriptions/api/useGetSubscription';
import { useGetServicePlan } from 'src/features/servicePlans/api/useGetServicePlan';
import { customersMessages } from 'src/features/customers/utils/messages';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { SectionBlock, SectionBlockHeader, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import DeleteSubscriptionFormModal from './forms/DeleteSubscriptionFormModal';
import UpdateSubscriptionFormModal from 'src/features/subscriptions/components/forms/UpdateSubscriptionFormModal';
import SubscriptionDashboardTables from 'src/features/subscriptions/components/SubscriptionDashboardTables';
import DashboardGeneralBlock from 'src/features/billing/components/DashboardGeneralBlock';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const SubscriptionDashboard = () => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { subscriptionId = '' } = useParams();

    const permissionManageSubscription = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);
    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionAccessServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(subscriptionsMessages.subscriptionDashboard));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const { data: subscription } = useGetSubscription({
        id: subscriptionId,
    });

    const { data: servicePlan } = useGetServicePlan({
        id: subscription?.service_plan.id ?? '',
        config: { enabled: !!subscription?.service_plan.id },
    });

    const isActiveAsString = servicePlan?.is_active ? 'true' : 'false';
    const isActiveBillingAsString = subscription?.has_active_billing ? 'true' : 'false';

    const dashboardBlockData = {
        section1: [
            {
                title: subscriptionsMessages.refNumber,
                value: subscription?.reference ?? '-',
            },
            {
                title: servicePlansMessages.category,
                value: servicePlan?.category?.name ?? '-',
            },
            {
                title: servicePlansMessages.servicePlan,
                value: subscription?.service_plan && (
                    <LinkWithIcon
                        to={`/service-plans/${subscription?.service_plan.id}`}
                        linkTitle={subscription?.service_plan.name}
                        hasPermissions={permissionAccessServicePlans}
                    />
                ),
            },
            {
                title: customersMessages.customer,
                value: subscription?.service_plan && (
                    <LinkWithIcon
                        to={`/customers/${subscription?.customer.id}`}
                        linkTitle={subscription?.customer.name}
                        hasPermissions={permissionAccessCustomers}
                    />
                ),
            },
            {
                title: subscriptionsMessages.externalId,
                value: subscription?.external_id ?? '-',
            },
            {
                title: servicePlansMessages.isActive,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveAsString].label)}
                    </Badge>
                ),
            },
            {
                title: subscriptionsMessages.activeBilling,
                value: (
                    <Badge theme={booleanBadgeOptions[isActiveBillingAsString].theme || 'gray'}>
                        <FontAwesomeIcon icon={booleanBadgeOptions[isActiveBillingAsString].icon as IconProp} />
                        {formatMessage(booleanBadgeOptions[isActiveBillingAsString].label)}
                    </Badge>
                ),
            },
        ],
        section2: [
            {
                title: subscriptionsMessages.startDate,
                value: subscription?.start_date ? getFormattedDateTimeForDetailPanel(subscription?.start_date) : '-',
            },
            {
                title: subscriptionsMessages.endDate,
                value: subscription?.end_date ? getFormattedDateTimeForDetailPanel(subscription?.end_date) : '-',
            },
            {
                title: subscriptionsMessages.cancelledAt,
                value: subscription?.cancelled_at
                    ? getFormattedDateTimeForDetailPanel(subscription?.cancelled_at)
                    : '-',
            },
            {
                title: subscriptionsMessages.lockedAt,
                value: subscription?.locked_at ? getFormattedDateTimeForDetailPanel(subscription?.locked_at) : '-',
            },
            {
                title: subscriptionsMessages.stoppedOn,
                value: subscription?.stopped_on ? getFormattedDateTimeForDetailPanel(subscription?.stopped_on) : '-',
            },
        ],
        section3: [
            {
                title: generalMessages.description,
                value: servicePlan?.description ?? '-',
            },
        ],
    };

    const dashboardTitle = `${subscription?.reference} - ${subscription?.service_plan.name} - ${subscription?.customer.name}`;

    return (
        <>
            <Button asChild className="place-self-start p-0" variant="link" onClick={() => navigate('/subscriptions')}>
                <div className="flex cursor-pointer">
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(subscriptionsMessages.backToAllSubscriptions)}
                </div>
            </Button>

            <div className="mt-6 grid grid-cols-1 gap-8">
                <SectionBlock>
                    <SectionBlockHeader>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{dashboardTitle}</SectionBlockTitle>
                        {permissionManageSubscription && subscription && (
                            <div className="flex flex-row gap-2">
                                <UpdateSubscriptionFormModal
                                    selectedSubscription={subscription}
                                    semantics={{
                                        entryName: formatMessage(subscriptionsMessages.subscription).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                                    </Button>
                                </UpdateSubscriptionFormModal>
                                <DeleteSubscriptionFormModal
                                    selectedSubscription={subscription}
                                    semantics={{
                                        entryName: formatMessage(subscriptionsMessages.subscription).toLowerCase(),
                                    }}
                                >
                                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeleteSubscriptionFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <DashboardGeneralBlock dashboardData={dashboardBlockData} indexForLastSeparator={2} />
                </SectionBlock>
                <SubscriptionDashboardTables />
            </div>
        </>
    );
};

export default SubscriptionDashboard;
